import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent, Button, CircularProgress, Chip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { Alert } from '@material-ui/lab'
import {
    getSamgovRequestCount,
    getSamgovIngestStatus,
    triggerSamgovIngest,
    uploadSamgovIngest,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const statTile = { padding: 12, minWidth: 140, textAlign: 'center' }
const statValue = { fontSize: 28, fontWeight: 600, color: '#222' }
const statLabel = { fontSize: 12, color: '#666', marginTop: 4 }

const statusChipStyle = (status) => {
    switch (status) {
        case 'SUCCESS':          return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'RUNNING':          return { background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }
        case 'FAILED':           return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        default:                 return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

const formatTs = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const SamgovUsage = () => {
    const [data, setData] = useState(null)
    const [ingest, setIngest] = useState(null)
    const [loading, setLoading] = useState(false)
    const [triggering, setTriggering] = useState(false)
    const fileInputRef = useRef(null)

    const fetch = useCallback(() => {
        setLoading(true)
        Promise.all([getSamgovRequestCount()(), getSamgovIngestStatus()()])
            .then(([usage, ingestStatus]) => {
                setData(usage)
                setIngest(ingestStatus)
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load SAM.gov usage' }))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { fetch() }, [fetch])

    // Auto-refresh every 15s while a run is in flight so the elapsed timer + api call count update live.
    useEffect(() => {
        if (ingest?.lastRun?.status !== 'RUNNING') return undefined
        const interval = setInterval(fetch, 15000)
        return () => clearInterval(interval)
    }, [ingest?.lastRun?.status, fetch])

    const onFileSelected = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        event.target.value = ''
        setTriggering(true)
        uploadSamgovIngest({ file })()
            .then(() => {
                Toast({ type: 'success', message: `Ingest started from ${file.name}` })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409 ? 'An ingest is already running' : 'Failed to upload file'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const runIngest = () => {
        setTriggering(true)
        triggerSamgovIngest()()
            .then(() => {
                Toast({ type: 'success', message: 'SAM.gov ingest started' })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409
                    ? 'An ingest is already running'
                    : 'Failed to trigger ingest'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const overQuota = data && typeof data.dailyFreeTierQuota === 'number' && data.todayUtc >= data.dailyFreeTierQuota
    const lastRun = ingest?.lastRun
    const isRunning = lastRun?.status === 'RUNNING'

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>SAM.gov Exclusions Usage</h4>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetch}
                    disabled={loading}
                    variant='outlined'
                    size='small'
                >
                    Refresh
                </Button>
            </div>

            <Alert severity='info' className='mb-3'>
                SAM.gov publishes the full exclusions extract on demand via a free-tier API (10
                requests/day). Each ingest burns 1&nbsp;request + up to 4 polls. Both{' '}
                <strong>Quick search</strong> and <strong>Ingest</strong> calls count against the
                daily quota.
            </Alert>

            {overQuota && (
                <Alert severity='warning' className='mb-3'>
                    Daily free-tier quota exceeded ({data.todayUtc} / {data.dailyFreeTierQuota}).
                    SAM.gov will reject further calls until {data.resetTime}.
                </Alert>
            )}

            {loading && !ingest && !data && (
                <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
            )}

            {ingest && (
                <Card variant='outlined' className='mb-3'>
                    <CardContent>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <h6 className='mb-0'>Local mirror &amp; ingest</h6>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    startIcon={<PlayArrowIcon />}
                                    onClick={runIngest}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    color='primary'
                                >
                                    {isRunning ? 'Running…' : 'Trigger ingest'}
                                </Button>
                                <Button
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    title='Ingest from a local CSV without burning the daily API quota'
                                >
                                    Upload CSV
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='.csv,text/csv'
                                    onChange={onFileSelected}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.totalRecords?.toLocaleString?.() ?? '—'}</div>
                                <div style={statLabel}>Total exclusions</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.activeRecords?.toLocaleString?.() ?? '—'}</div>
                                <div style={statLabel}>Active</div>
                            </div>
                        </div>

                        {!lastRun && (
                            <div style={{ fontSize: 13, color: '#666' }}>No ingest run recorded yet.</div>
                        )}

                        {lastRun && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <strong style={{ fontSize: 13 }}>Last run:</strong>
                                    <Chip label={lastRun.status} size='small' style={statusChipStyle(lastRun.status)} />
                                    {isRunning && <CircularProgress size={16} />}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13 }}>
                                    <strong>Started:</strong><span>{formatTs(lastRun.startedAt)}</span>
                                    <strong>Finished:</strong><span>{formatTs(lastRun.finishedAt)}</span>
                                    <strong>Elapsed:</strong><span>{lastRun.elapsed ?? '—'}</span>
                                    <strong>Records upserted:</strong><span>{lastRun.recordsUpserted?.toLocaleString?.() ?? lastRun.recordsUpserted ?? 0}</span>
                                    <strong>Records removed:</strong><span>{lastRun.recordsRemoved?.toLocaleString?.() ?? lastRun.recordsRemoved ?? 0}</span>
                                    {lastRun.rowsRead != null && (
                                        <>
                                            <strong>Rows read:</strong>
                                            <span>{lastRun.rowsRead?.toLocaleString?.() ?? lastRun.rowsRead}</span>
                                        </>
                                    )}
                                    {lastRun.rowsDedupSkipped != null && (
                                        <>
                                            <strong>Rows skipped (dedup):</strong>
                                            <span>{lastRun.rowsDedupSkipped?.toLocaleString?.() ?? lastRun.rowsDedupSkipped}</span>
                                        </>
                                    )}
                                    {lastRun.rowsMalformedSkipped != null && (
                                        <>
                                            <strong>Rows skipped (malformed):</strong>
                                            <span>{lastRun.rowsMalformedSkipped?.toLocaleString?.() ?? lastRun.rowsMalformedSkipped}</span>
                                        </>
                                    )}
                                    {typeof lastRun.apiCallsThisRun === 'number' && (
                                        <>
                                            <strong>API calls (this run):</strong>
                                            <span>{lastRun.apiCallsThisRun} of ~5 expected (1 request + up to 4 polls)</span>
                                        </>
                                    )}
                                    {typeof lastRun.apiCallCount === 'number' && lastRun.status !== 'RUNNING' && (
                                        <>
                                            <strong>API calls burned:</strong>
                                            <span>{lastRun.apiCallCount} (counted against today's quota)</span>
                                        </>
                                    )}
                                    {lastRun.errorMessage && (
                                        <>
                                            <strong style={{ color: 'var(--status-declined)' }}>Error:</strong>
                                            <span style={{ color: 'var(--status-declined)', wordBreak: 'break-word' }}>{lastRun.errorMessage}</span>
                                        </>
                                    )}
                                </div>

                                {isRunning && (
                                    <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                                        Auto-refreshing every 15 seconds while the run is in flight.
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {data && (
                <Card variant='outlined' className='mb-3'>
                    <CardContent>
                        <h6 className='mb-3'>API usage (SAM.gov specific)</h6>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                            <div style={statTile}>
                                <div style={statValue}>{data.todayUtc}</div>
                                <div style={statLabel}>Today (UTC)</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{data.last24h}</div>
                                <div style={statLabel}>Last 24 hours</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{data.last7d}</div>
                                <div style={statLabel}>Last 7 days</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{data.last30d}</div>
                                <div style={statLabel}>Last 30 days</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{data.total}</div>
                                <div style={statLabel}>All time</div>
                            </div>
                        </div>

                        {data.todayBreakdown && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                                <strong style={{ fontSize: 13 }}>By source (today):</strong>
                                <Chip
                                    label={`Quick search: ${data.todayBreakdown.quickSearchToday}`}
                                    size='small'
                                    style={{ background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }}
                                />
                                <Chip
                                    label={`Ingest: ${data.todayBreakdown.ingestToday}`}
                                    size='small'
                                    style={{ background: '#f3e5f5', color: '#6a1b9a' }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 13 }}>By outcome (today):</strong>
                            <Chip
                                label={`Successful: ${data.todayUtcSuccessful}`}
                                size='small'
                                style={{ background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }}
                            />
                            <Chip
                                label={`Errors: ${data.todayUtcErrors}`}
                                size='small'
                                style={{ background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {(ingest || data) && (
                <Card variant='outlined'>
                    <CardContent>
                        <h6 className='mb-2'>Reference</h6>
                        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                            <div><strong>Source:</strong> <a href='https://sam.gov/data-services' target='_blank' rel='noopener noreferrer'>sam.gov/data-services</a></div>
                            <div><strong>Cost:</strong> free — API key required, 10 requests/day free-tier quota</div>
                            {data && <div><strong>Free tier daily limit:</strong> {data.dailyFreeTierQuota} requests</div>}
                            {data && <div><strong>Next reset:</strong> {data.resetTime} (UTC midnight)</div>}
                            <div><strong>Scheduler:</strong> disabled by default in non-prod (<code>roxwrite.scheduler.samgov-ingest.enabled</code>)</div>
                            <div><strong>Cadence:</strong> extract is on-demand; scheduler runs daily in prod</div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    )
}

export default SamgovUsage
